import {
    Troubleshoot,
    InterpreterMode,
    Assignment,
    FavoriteBorder,
    MonitorHeartOutlined,
    Support,
    BeachAccess,
    SickOutlined,
    Weekend,
    Flag,
    AccessibilityNew,
    FamilyRestroom,
} from "@mui/icons-material";
import { Box, SxProps, Typography } from "@mui/material";
import { Avatar } from "../../../components/fieldsViews/fields/Avatar";
import { CustomEventType } from "../utils/types";
type ScheduleCustomEventProps = {
    event: CustomEventType;
};
export const ScheduleCustomEvent = ({ event }: ScheduleCustomEventProps) => {
    if (event.allDay || event.type === "time_off") {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", height: 15, width: "100%", gap: 1 }}>
                {event.userAvatar ? (
                    <Avatar 
                        value={event.userAvatar} 
                        styles={{ 
                            width: 15, 
                            height: 15,
                            border: event.type === "schedule" && event.clinicColor ? 2 : 0,
                            borderColor: event.type === "schedule" ? event.clinicColor : undefined,
                        }} 
                    />
                ) : (
                    <Typography
                        sx={{
                            fontSize: 8,
                            padding: 0,
                            margin: 0,
                            maxWidth: "100%",
                        }}
                    >
                        {event.userName}
                    </Typography>
                )}
                {event.type === "time_off" && <TimeOffIcon type={event.time_off_type || ""} styles={{ fontSize: "14px" }} />}
            </Box>
        );
    }

    const withServices = event.services && event.services.length > 0 ? event.services : null;

    return (
        <Box
            display="flex"
            alignItems="flex-start"
            flexDirection="column"
            gap={2}
            width="100%"
            sx={{
                position: "relative",
                mt: 1,
                userSelect: "none",
                "& img": {
                    pointerEvents: "none",
                },
            }}
            draggable="false"
        >
            {event.userAvatar && (
                <Box draggable="false">
                    <Avatar 
                        value={event.userAvatar} 
                        styles={{ 
                            ml: 1, 
                            width: 25, 
                            height: 25,
                            border: event.clinicColor ? 2 : 0,
                            borderColor: event.clinicColor,
                        }} 
                    />
                </Box>
            )}
            {withServices ? (
                withServices.map((service_id: number) => <ServiceIcon service_id={service_id} key={service_id} />)
            ) : event.type === "schedule" ? (
                <Assignment />
            ) : null}

            <Typography variant="caption" sx={{ width: "100%", wordBreak: "break-word" }}>
                {event.notes}
            </Typography>
        </Box>
    );
};

type ServiceIconProps = {
    service_id?: number;
    styles?: SxProps;
};

export const ServiceIcon = ({ service_id, styles }: ServiceIconProps) => {
    switch (service_id) {
        case 33:
            return <MonitorHeartOutlined sx={styles} />;
        case 35:
        case 39:
            return <Troubleshoot sx={styles} />;
        case 34:
        case 36:
            return <FavoriteBorder sx={styles} />;
        case 37:
            return <AccessibilityNew sx={styles} />;
        case 40:
            return <InterpreterMode sx={styles} />;
        case 50:
            return <Support sx={styles} />;
        case 52:
            return <FamilyRestroom sx={styles} />;
        case 0:
            return <Assignment sx={styles} />;

        default:
            return null;
    }
};

export const TimeOffIcon = ({ type, styles }: { type: string; styles: SxProps }) => {
    switch (type) {
        case "holiday":
            return <BeachAccess sx={styles} />;
        case "sick_leave":
            return <SickOutlined sx={styles} />;
        case "day_off":
            return <Weekend sx={styles} />;
        case "public_holiday":
            return <Flag sx={styles} />;
        default:
            return null;
    }
};

// Compact component for month view
export const ScheduleCustomEventMonth = ({ event }: ScheduleCustomEventProps) => {
    const withServices = event.services && event.services.length > 0 ? event.services : null;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                width: "100%",
                height: "100%",
                minHeight: "18px",
                overflow: "hidden",
            }}
        >
            {event.userAvatar && (
                <Avatar 
                    value={event.userAvatar} 
                    styles={{ 
                        width: 16, 
                        height: 16, 
                        flexShrink: 0,
                        border: event.type === "schedule" && event.clinicColor ? 2 : 0,
                        borderColor: event.type === "schedule" ? event.clinicColor : undefined,
                    }} 
                />
            )}
            <Typography
                variant="caption"
                sx={{
                    fontSize: "10px",
                    lineHeight: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                }}
            >
                {event.userName || event.notes}
            </Typography>

            {withServices && (
                <Box display="flex" gap={0.25}>
                    {withServices.map((service_id: number) => (
                        <ServiceIcon service_id={service_id} key={service_id} styles={{ fontSize: "14px" }} />
                    ))}
                </Box>
            )}

            {event.type === "time_off" && <TimeOffIcon type={event.time_off_type || ""} styles={{ fontSize: "14px" }} />}
        </Box>
    );
};
